import type { FC } from 'react';
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';
import { getFocalAndComparisonAggregates } from 'src/components/dashboard/helper';

import { styles } from './index.styles';

const BarDiagramWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { data, errorMsg, refetch, loading } = useStatistics();
  const focalAndComparisonAggregates = getFocalAndComparisonAggregates(
    config,
    data,
  );

  const rows = config.questions.map((_, i) => {
    const agg = focalAndComparisonAggregates[i]?.focalAggregate;
    const raw = Number.isFinite(agg?.ptop2 as number)
      ? (agg?.ptop2 as number)
      : 0;
    const value = Math.max(0, Math.min(100, Math.round(raw)));
    return { label: agg?.label ?? '', value };
  });

  const theme = useTheme();
  const sortedRows = [...rows].sort((a, b) =>
    config.sort_by_value ? b.value - a.value : 0,
  );

  if (loading || errorMsg) {
    return (
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Stack
          direction="column"
          spacing={2}
          data-test-id="loading-bar-diagram"
          sx={{
            p: 2,
          }}
        >
          <Skeleton variant="rectangular" height={40} />
          {config.questions.map((_, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Skeleton variant="text" width="25%" />
              <Skeleton variant="rectangular" width="50%" height={30} />
              <Skeleton variant="text" width="20%" />
            </Stack>
          ))}
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        flexGrow: 1,
        maxWidth: '100%',
        position: 'relative',
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {config.name}
          </Typography>
        </Grid>
        {sortedRows.map((row, i) => (
          <Grid
            container
            item
            spacing={1}
            key={i}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid item xs={4} md={3}>
              <Typography variant="body2">{row.label}</Typography>
            </Grid>
            <Grid item xs={8} md={9}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <Box sx={styles.barRow}>
                  <Box sx={styles.bar(theme, row.value)}>
                    <Typography variant="body2" sx={styles.percentLabel}>
                      {row.value}%
                    </Typography>
                  </Box>
                  <Box sx={styles.barRest(theme, 100 - row.value)} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const BarDiagramWidgetWrapper: FC<DashboardWidgetProps> = ({ config }) => {
  if (config.questions.length === 0) {
    return null;
  }
  return <BarDiagramWidget config={config} />;
};

export default BarDiagramWidgetWrapper;
